/**
 * Canonical event-local date/time utilities for hosts and API mapping.
 *
 * Rules:
 * - Never parse calendar `YYYY-MM-DD` as UTC (`new Date("YYYY-MM-DD")`).
 * - Display and pickers use wall fields: `startDateLocal`, optional `startTimeLocal` (`HH:mm`), `eventTimeZone` (IANA).
 * - `startAtUtc` is derived for machine use only; do not reconstruct host picks from it for edit prefill.
 */

import type { EventData } from "@/lib/events/types"
import { hhmmTo12hLabelEnUs } from "@/lib/events/wall-time"

const LOCALE = "en-US" as const

/** Parses strict `YYYY-MM-DD` as a calendar date (not UTC midnight). */
export function parseIsoDateOnly(str: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str.trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  return { y, m: mo, d }
}

/** Host device local calendar date → `YYYY-MM-DD` (create/save; not UTC-derived). */
export function localDateToYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** `YYYY-MM-DD` → local calendar `Date` at that civil date (edit/create pickers). */
export function ymdToLocalCalendarDate(ymd: string): Date | null {
  const p = parseIsoDateOnly(ymd.trim())
  if (!p) return null
  const d = new Date(p.y, p.m - 1, p.d)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Start of civil calendar day in the **runtime local** timezone (sorting / buckets only). */
export function calendarYmdStartLocalMs(dateStr: string): number | null {
  const p = parseIsoDateOnly(dateStr?.trim() ?? "")
  if (!p) return null
  const d = new Date(p.y, p.m - 1, p.d)
  d.setHours(0, 0, 0, 0)
  const t = d.getTime()
  return Number.isNaN(t) ? null : t
}

function dateForLocaleFormat(startDateLocal: string): Date | null {
  const raw = startDateLocal?.trim() ?? ""
  if (!raw) return null
  const iso = parseIsoDateOnly(raw)
  if (iso) {
    const d = new Date(iso.y, iso.m - 1, iso.d)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Prefer persisted local calendar date; never derive from `startAtUtc`. */
export function getEventPrefillDateYmd(
  event: Pick<EventData, "startDateLocal" | "date">,
): string {
  return event.startDateLocal?.trim() || event.date?.trim() || ""
}

/**
 * Time picker label: canonical `startTimeLocal` (`HH:mm`) → `h:mm AM/PM`, else legacy `time` string.
 * Never uses `startAtUtc`.
 */
export function getEventPrefillTimePickerLabel(
  event: Pick<EventData, "startTimeLocal" | "time">,
): string | null {
  const hm = event.startTimeLocal?.trim()
  if (hm) {
    const label = hhmmTo12hLabelEnUs(hm)
    if (label) return label
  }
  const t = event.time?.trim()
  return t || null
}

/** API / cards: always format using wall date (resolved `date` on DTO equals `startDateLocal` when persisted). */
export function formatEventSurfaceDateLong(
  event: Pick<EventData, "startDateLocal" | "date">,
): string {
  const ymd = getEventPrefillDateYmd(event)
  return formatWeekdayLongMonthDayYear(ymd)
}

export function formatEventSurfaceDateShort(
  event: Pick<EventData, "startDateLocal" | "date">,
): string {
  const ymd = getEventPrefillDateYmd(event)
  return formatDayShortMonthDay(ymd)
}

/** e.g. "Apr 8" — notifications */
export function formatNotificationEventDateLabel(startDateLocal: string): string {
  const d = dateForLocaleFormat(startDateLocal)
  if (!d) return ""
  return d.toLocaleDateString(LOCALE, { month: "short", day: "numeric" })
}

/** e.g. "Mon, Mar 4" — lists */
export function formatDayShortMonthDay(startDateLocal: string): string {
  const d = dateForLocaleFormat(startDateLocal)
  if (!d) return ""
  return d.toLocaleDateString(LOCALE, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

/** Compact date + optional wall time label (as stored / shown to host). */
export function formatEventDateTimeLine(
  startDateLocal: string,
  timeStr?: string | null,
): string {
  const raw = startDateLocal?.trim()
  if (!raw) return ""
  const label = formatDayShortMonthDay(raw)
  const t = timeStr?.trim()
  return t ? `${label} · ${t}` : label
}

/** e.g. "Wednesday, March 4, 2026" — detail blocks */
export function formatWeekdayLongMonthDayYear(startDateLocal: string): string {
  const d = dateForLocaleFormat(startDateLocal)
  if (!d) return ""
  return d.toLocaleDateString(LOCALE, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

/** e.g. "March 2026" — archive headers */
export function formatMonthYearHeader(startDateLocal: string): string {
  const d = dateForLocaleFormat(startDateLocal)
  if (!d) return ""
  return d.toLocaleDateString(LOCALE, {
    month: "long",
    year: "numeric",
  })
}

/** e.g. "Wed, Mar 4" — archive rows */
export function formatArchiveRowDate(startDateLocal: string): string {
  const d = dateForLocaleFormat(startDateLocal)
  if (!d) return ""
  return d.toLocaleDateString(LOCALE, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

/** Selected date on host date button (no year). */
export function formatPickerDateCompact(date: Date): string {
  return date.toLocaleDateString(LOCALE, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

/** Selected date in create date modal (includes year). */
export function formatPickerDateWithWeekday(date: Date): string {
  return date.toLocaleDateString(LOCALE, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}
